import React from 'react';

import { TableCell, TableRow } from '@mui/material';
import { flexRender, type Row } from '@tanstack/react-table';

import type { SxProp } from 'src/types/mui';
import { composeSx } from 'src/utils/styles';

import type { DefaultDataRow, TableColumn, TableThemes } from '../../../types';
import { GRID_ROW_ERROR_CLASSNAME, SELECTION_COLUMN_ID } from '../../constants';
import type { TableFeatureSet } from '../../features';
import { columnKindStyles, getColumnKind, TableStyles } from '../../theme';
import type { TableProps } from '../../types';

type TableBodyRowProps<TRecord extends DefaultDataRow> = {
  row: Row<TableFeatureSet, TRecord>;
  selected: boolean;
  className?: string;
  tableTheme: TableThemes;
  columnsByKey: Map<string, TableColumn<TRecord>>;
  onRowClick?: TableProps<TRecord>['onRowClick'];
};

type TableBodyRowComponent = <TRecord extends DefaultDataRow>(
  props: TableBodyRowProps<TRecord>,
) => React.ReactElement;

// memo keeps a selection toggle from re-rendering every row; the impl is inlined into memo
// (rather than a standalone component) so the module's only component export is the memoized
// one, and the cast restores the generic signature memo() erases
export const TableBodyRow = React.memo(
  <TRecord extends DefaultDataRow>({
    row,
    selected,
    className,
    tableTheme,
    columnsByKey,
    onRowClick,
  }: TableBodyRowProps<TRecord>) => {
    return (
      <TableRow
        hover
        selected={selected}
        className={className}
        onClick={
          onRowClick &&
          ((event: React.MouseEvent<HTMLTableRowElement>) => {
            // clicks bubbling through React portals (e.g. the row-actions Menu backdrop) reach
            // the row handler but their target is outside the <tr> — those are not row clicks
            if (!event.currentTarget.contains(event.target as Node)) {
              return;
            }
            onRowClick({ id: row.id, row: row.original }, event);
          })
        }
        sx={composeSx(bodyRowStyles, onRowClick ? clickableRowStyles : undefined)}
      >
        {row.getAllCells().map(cell => {
          const tableColumn = columnsByKey.get(cell.column.id);

          return (
            <TableCell
              key={cell.id}
              padding={cell.column.id === SELECTION_COLUMN_ID ? 'checkbox' : tableColumn?.padding}
              align={tableColumn?.align}
              sx={composeSx(
                bodyCellStyles(tableTheme),
                columnKindStyles[getColumnKind(cell.column.id)],
                tableColumn?.sx,
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        })}
      </TableRow>
    );
  },
) as TableBodyRowComponent;

const bodyCellStyles =
  (tableTheme: TableThemes): SxProp =>
  theme => ({
    padding: TableStyles[tableTheme].tableCellPadding,
    // fixed table layout sizes columns from the header row; the table-level minWidth on
    // <MuiTable> replaces the old per-cell minWidth. overflow + wordBreak keep long content
    // wrapping inside the fixed width instead of forcing the column wider.
    overflow: 'hidden',
    borderColor: theme.palette.border?.main,
    verticalAlign: 'middle',
    '& p': {
      wordBreak: 'break-word',
    },
  });

// the sticky actions cell paints with backgroundColor: 'inherit', so every row state must be
// an OPAQUE color — MUI's default hover/selected backgrounds are translucent rgba, which lets
// the scrolled columns show through the pinned cell. color-mix flattens each state onto white.
const bodyRowStyles: SxProp = theme => {
  const { white } = theme.palette.common;
  const { hoverOpacity, selectedOpacity } = theme.palette.action;
  const mixOnWhite = (color: string, opacity: number) =>
    `color-mix(in srgb, ${color} ${opacity * 100}%, ${white})`;

  return {
    backgroundColor: white,
    '&.MuiTableRow-hover:hover': {
      backgroundColor: mixOnWhite(theme.palette.common.black, hoverOpacity),
    },
    '&.Mui-selected': {
      backgroundColor: mixOnWhite(theme.palette.primary.main, selectedOpacity),
    },
    '&.Mui-selected:hover': {
      backgroundColor: mixOnWhite(theme.palette.primary.main, selectedOpacity + hoverOpacity),
    },
    [`&.${GRID_ROW_ERROR_CLASSNAME}`]: {
      backgroundColor: mixOnWhite('rgb(211, 47, 47)', 0.12),
    },
    [`&.${GRID_ROW_ERROR_CLASSNAME}:hover`]: {
      backgroundColor: mixOnWhite('rgb(211, 47, 47)', 0.2),
    },
  };
};

const clickableRowStyles: SxProp = {
  cursor: 'pointer',
};
